import { useTranslation } from "react-i18next";
import type { ParticipantRole } from "../hooks/useParticipantRole";

/**
 * Small role indicator for a call participant (#215). Three visual weights so
 * the hierarchy reads at a glance in a tile/roster row without needing a
 * legend: Admin is filled (highest authority), User is a subtle tinted pill
 * (the common case), Guest is an outline (lowest-trust, unauthenticated).
 *
 * `twin` (the Mochi meeting twin, an AI participant) gets its own filled
 * brand-accent pill so it's visually distinct from a real Admin — per the
 * branding rules, this uses `var(--accent)`/`var(--text-accent)` rather than
 * a hardcoded hue (see CLAUDE.md "Tenant branding").
 */
const ROLE_CLASSES: Record<ParticipantRole, string> = {
  admin: "bg-primary text-primary-foreground",
  user: "bg-muted text-muted-foreground",
  guest: "border border-border text-muted-foreground bg-transparent",
  twin: "bg-[var(--accent)] text-[var(--color-primary-foreground,#000)]",
};

export function RoleBadge({ role, className = "" }: { role: ParticipantRole; className?: string }) {
  const { t } = useTranslation();

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${ROLE_CLASSES[role]} ${className}`}
    >
      {t(`call.role.${role}`)}
    </span>
  );
}
